/* =============================================================================
   CORE / DISH-GENERATOR — Generativer Gericht-Entwurf (S156 / Phase D2, Stufe 1)
   =============================================================================
   Pure-Heuristik (kein LLM): stellt aus dem Zutaten-/Options-Katalog eine sinnvolle
   neue Gericht-Kombination zusammen.
     - RESPEKTIERT Constraints (vegan/vegetarisch/glutenfrei/laktosefrei/≤kcal).
     - GESCHMACKSMUSTER: Zutaten, die in bestehenden Rezepten zusammen vorkommen,
       bekommen höheres Gewicht (Ko-Vorkommen aus recipesByItem).
     - Slot-Vorlage je Bauart (Burger/Bowl/Salat/Getränk/Beilage/Soße).
   Deterministisch testbar über optionalen `rng` (seedbar). Nährwerte über den
   gemeinsamen A2-Helfer composeNutrition (SQL+JS-deckungsgleich).
============================================================================ */

import { composeNutrition, qtyToBase } from './nutrition.js';

const _DRAFT = '__gen_draft__';

// Slot-Kategorie → Keyword-Matcher (auf ingredient.category/subcategory/name).
const SLOT_KW = {
  cheese:   ['käse', 'kase', 'cheese', 'cheddar', 'feta', 'mozzarella', 'gouda', 'parmesan'],
  veg:      ['gemüse', 'gemuse', 'salat', 'tomate', 'zwiebel', 'gurke', 'rucola', 'paprika', 'mais', 'jalapeno', 'jalapeño', 'avocado', 'spinat', 'kraut', 'pilz', 'lauch', 'rettich', 'karotte'],
  sauce:    ['soße', 'sosse', 'sauce', 'mayo', 'ketchup', 'senf', 'aioli', 'pesto', 'dip', 'guacamole'],
  protein:  ['fleisch', 'patty', 'rind', 'hähnch', 'hahnch', 'huhn', 'pute', 'fisch', 'lachs', 'thunfisch', 'bacon', 'schinken', 'wurst', 'falafel', 'tofu', 'protein', 'garnele', 'shrimp', 'halloumi', 'ei'],
  carb:     ['sättigung', 'sattigung', 'beilage', 'reis', 'quinoa', 'kartoffel', 'pommes', 'nudel', 'bulgur', 'couscous', 'brot'],
  dressing: ['dressing', 'vinaigrette']
};

const TEMPLATES = {
  burger: [
    { slot: 'cheese', min: 0, max: 1, qty: [20, 30], optional: true },
    { slot: 'veg',    min: 1, max: 2, qty: [15, 25] },
    { slot: 'sauce',  min: 1, max: 1, qty: [10, 20] }
  ],
  bowl: [
    { slot: 'carb',     min: 1, max: 1, qty: [120, 180] },
    { slot: 'protein',  min: 1, max: 1, qty: [100, 140] },
    { slot: 'veg',      min: 1, max: 2, qty: [30, 50] },
    { slot: 'dressing', min: 1, max: 1, qty: [20, 30] }
  ],
  salad: [
    { slot: 'veg',      min: 2, max: 3, qty: [30, 60] },
    { slot: 'protein',  min: 0, max: 1, qty: [60, 100], optional: true },
    { slot: 'cheese',   min: 0, max: 1, qty: [20, 30], optional: true },
    { slot: 'dressing', min: 1, max: 1, qty: [20, 30] }
  ],
  drink: [{ slot: 'any', min: 1, max: 1, qty: [200, 300] }],
  side:  [{ slot: 'carb', min: 1, max: 1, qty: [120, 180] }],
  sauce: [{ slot: 'sauce', min: 1, max: 1, qty: [20, 40] }]
};

const ADJECTIVES = ['Herzhafter', 'Frischer', 'Würziger', 'Klassischer', 'Grüner', 'Feuriger', 'Saftiger', 'Bunter'];
const KIND_SUFFIX = { burger: '-Burger', bowl: ' Bowl', salad: ' Salat', drink: '', side: '', sauce: '' };
// Constraint-Tag (englisch, aus Zutaten/Nährwerten) → Karten-Tag (deutsch).
const CARD_TAG = { vegan: 'vegan', vegetarian: 'vegetarisch', gluten_free: 'glutenfrei', lactose_free: 'laktosefrei' };

function _hay(ing) {
  return (String(ing.category || '') + ' ' + String(ing.subcategory || '') + ' ' + String(ing.name || '')).toLowerCase();
}
function ingMatchesSlot(ing, slot) {
  if (slot === 'any') return true;
  const kw = SLOT_KW[slot] || [];
  const hay = _hay(ing);
  return kw.some(k => hay.includes(k));
}
function passesConstraints(ing, c) {
  const tags = ing.dietaryTags || [];
  const al = ing.allergens || [];
  if (c.vegan && !tags.includes('vegan')) return false;
  if (c.vegetarian && !(tags.includes('vegetarian') || tags.includes('vegan'))) return false;
  if (c.gluten_free && al.includes('gluten')) return false;
  if (c.lactose_free && al.includes('milk')) return false;
  return true;
}
// Eine Option (Bun/Patty) ist constraint-konform, wenn ALLE ihre Rezept-Zutaten passen.
function optionPasses(optionId, recipesByItem, ingredientsById, c) {
  const rows = recipesByItem[optionId] || [];
  if (!rows.length) return false;
  return rows.every(r => {
    const ing = ingredientsById[r.ingredientId];
    return ing && passesConstraints(ing, c);
  });
}
function hasDietConstraint(c) {
  return !!(c && (c.vegan || c.vegetarian || c.gluten_free || c.lactose_free));
}
// S156b/A — Default-Option einer Options-Gruppe (Brötchen/Patty/Dressing …):
// Bei aktivem Diät-Constraint NUR constraint-konforme Optionen zulassen (Rezept-
// Zutaten erfüllen den Constraint) — keine konform → null (Gruppe-Default weglassen,
// das Default darf den Constraint NIE brechen). Sonst das als Default markierte,
// ersatzweise gewichtet/zufällig unter den zulässigen.
function pickGroupDefault(group, recipesByItem, ingredientsById, c, rng) {
  const opts = (group && group.options) || [];
  if (!opts.length) return null;
  let pool = opts;
  if (hasDietConstraint(c)) {
    pool = opts.filter(o => optionPasses(o.id, recipesByItem, ingredientsById, c));
    if (!pool.length) return null; // keine konforme Option → Gruppe weglassen
  }
  const def = pool.find(o => o.isDefault);
  if (def) return def; // konformes Default bevorzugt
  return pool[randInt(0, pool.length - 1, rng)];
}

// Ko-Vorkommens-Zähler: für jedes Rezept zählen alle ungeordneten Zutat-Paare.
function buildPairCounts(recipesByItem) {
  const counts = new Map();
  for (const rows of Object.values(recipesByItem || {})) {
    const ids = [...new Set((rows || []).map(r => r.ingredientId).filter(Boolean))];
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const key = ids[i] < ids[j] ? ids[i] + '|' + ids[j] : ids[j] + '|' + ids[i];
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
  }
  return counts;
}
function pairCount(counts, a, b) {
  const key = a < b ? a + '|' + b : b + '|' + a;
  return counts.get(key) || 0;
}

function randInt(lo, hi, rng) { return lo + Math.floor(rng() * (hi - lo + 1)); }
function randQty(ing, range, rng) {
  if (ing && ing.baseUnit === 'piece') return randInt(1, 2, rng);
  const q = range[0] + rng() * (range[1] - range[0]);
  return Math.max(1, Math.round(q / 5) * 5); // auf 5 g/ml runden
}

// Gewichtete Zufallswahl: Gewicht = 1 + Summe der Ko-Vorkommen mit bereits Gewähltem.
function weightedPick(candidates, chosenIds, counts, rng) {
  const weights = candidates.map(ci => 1 + chosenIds.reduce((s, cid) => s + pairCount(counts, ci.id, cid), 0));
  const total = weights.reduce((s, w) => s + w, 0);
  let r = rng() * total;
  for (let i = 0; i < candidates.length; i++) {
    r -= weights[i];
    if (r <= 0) return candidates[i];
  }
  return candidates[candidates.length - 1];
}

/**
 * Generiert einen Gericht-Entwurf.
 * @param {object} args
 * @param {string} args.kind - 'burger'|'bowl'|'salad'|'drink'|'side'|'sauce'
 * @param {Array}  args.optionGroups - aus menu-configurator.optionGroupsFromMenu
 * @param {object} args.constraints - { vegan, vegetarian, gluten_free, lactose_free, maxKcal }
 * @param {Object<string,object>} args.ingredientsById
 * @param {Object<string,Array>}  args.recipesByItem - inkl. Options-Rezepte
 * @param {function} [args.rng] - 0..1, seedbar (default Math.random)
 * @param {object} [args.keep] - S156b/B selektives Re-Roll: bereits gesetzte Slots,
 *   die NICHT überschrieben werden ({ name, baseRows:[…], bun:<optionId>, patty:<optionId|name> }).
 * @returns {{baseRows,meta,suggestedName,dietaryTags,nutrition,usedOptions}}
 */
export function generateDish(args) {
  const { kind = 'salad', optionGroups = [], constraints = {}, ingredientsById = {}, recipesByItem = {} } = args || {};
  const rng = (typeof args.rng === 'function') ? args.rng : Math.random;
  const keep = (args && args.keep) || {};
  const c = constraints || {};
  const maxKcal = (c.maxKcal != null && c.maxKcal !== '' && isFinite(Number(c.maxKcal))) ? Number(c.maxKcal) : null;

  const allIngredients = Object.values(ingredientsById);
  const pool = allIngredients.filter(ing => passesConstraints(ing, c));
  const counts = buildPairCounts(recipesByItem);
  const template = TEMPLATES[kind] || TEMPLATES.salad;

  // S156b/B — Basis-Rezept: getouchte Zeilen behalten (selektiv), sonst würfeln.
  const baseKept = Array.isArray(keep.baseRows) && keep.baseRows.length > 0;
  const baseRows = [];
  const chosen = [];
  if (baseKept) {
    for (const r of keep.baseRows) { baseRows.push({ ...r }); if (r && r.ingredientId) chosen.push(r.ingredientId); }
  } else {
    for (const slotDef of template) {
      const n = randInt(slotDef.min, slotDef.max, rng);
      for (let k = 0; k < n; k++) {
        const candidates = pool.filter(ing => ingMatchesSlot(ing, slotDef.slot) && !chosen.includes(ing.id));
        if (!candidates.length) continue;
        const pick = weightedPick(candidates, chosen, counts, rng);
        baseRows.push({ ingredientId: pick.id, quantity: randQty(pick, slotDef.qty, rng), unit: 'base', isOptional: !!slotDef.optional });
        chosen.push(pick.id);
      }
    }
  }

  // Optionen (nur Burger): keep > constraint-konformes Default (S156b/A).
  const meta = {};
  const usedOptions = {};
  if (kind === 'burger') {
    const bunG = optionGroups.find(g => g.role === 'bun');
    const pattyG = optionGroups.find(g => g.role === 'patty');
    const extraG = optionGroups.find(g => g.role === 'extras');
    if (bunG) {
      meta.bunGroup = bunG.group; meta.isBurger = true;
      let bun = keep.bun ? bunG.options.find(o => o.id === keep.bun) : null;
      if (!bun) bun = pickGroupDefault(bunG, recipesByItem, ingredientsById, c, rng);
      // S156c — Default-Bun als Name in meta (symmetrisch zum Patty) → Live-Panel/RPC
      // lösen den constraint-konformen Bun auf, nicht den Gruppen-Default (Brioche).
      if (bun) { usedOptions.bun = bun.id; if (bun.name) meta.defaultBun = bun.name; }
    }
    if (pattyG) {
      meta.pattyGroup = pattyG.group; meta.allowPattySwap = true;
      let patty = keep.patty ? pattyG.options.find(o => o.id === keep.patty || o.name === keep.patty) : null;
      if (!patty) patty = pickGroupDefault(pattyG, recipesByItem, ingredientsById, c, rng);
      if (patty) { meta.defaultPatty = patty.name; usedOptions.patty = patty.id; }
    }
    if (extraG) meta.extrasGroup = extraG.group;
  }

  const defaults = { bun: usedOptions.bun || null, patty: usedOptions.patty || null };
  const computeNutri = rows => composeNutrition(_DRAFT, {}, {
    recipesByItem: Object.assign({}, recipesByItem, { [_DRAFT]: rows }),
    ingredientsById, defaults
  });

  // ≤kcal: kcal-stärkste OPTIONALE Basis-Zutat streichen, bis unter dem Limit
  // (oder keine optionale mehr da). Begrenzt, deterministisch. Behaltene Basis
  // (keep.baseRows) wird NICHT getrimmt — das sind die Zeilen des Users.
  let nutrition = computeNutri(baseRows);
  if (maxKcal != null && !baseKept) {
    let guard = 0;
    while (nutrition.energyKcal > maxKcal && guard++ < 12) {
      const optIdx = baseRows
        .map((r, i) => ({ i, r }))
        .filter(x => x.r.isOptional)
        .map(x => {
          const ing = ingredientsById[x.r.ingredientId];
          const g = ing ? qtyToBase(ing, x.r.quantity, x.r.unit, []) : 0;
          return { i: x.i, kcal: (g || 0) / 100 * ((ing && ing.energyKcal) || 0) };
        })
        .sort((a, b) => b.kcal - a.kcal)[0];
      if (!optIdx) break; // keine optionale Zutat mehr → Entwurf bleibt (Chip rot)
      baseRows.splice(optIdx.i, 1);
      nutrition = computeNutri(baseRows);
    }
  }

  const dietaryTags = (nutrition.dietaryTags || []).map(t => CARD_TAG[t]).filter(Boolean);
  const suggestedName = keep.name ? keep.name : buildName(kind, baseRows, usedOptions, ingredientsById, recipesByItem, rng);

  return { baseRows, meta, suggestedName, dietaryTags, nutrition, usedOptions };
}

function buildName(kind, baseRows, usedOptions, ingredientsById, recipesByItem, rng) {
  const adj = ADJECTIVES[randInt(0, ADJECTIVES.length - 1, rng)];
  // Leitzutat: Patty-Name (Burger) sonst erste Nicht-optionale Basis-Zutat.
  let lead = '';
  if (kind === 'burger' && usedOptions.patty) {
    const pr = recipesByItem[usedOptions.patty] || [];
    const ing = pr.length ? ingredientsById[pr[0].ingredientId] : null;
    lead = ing ? ing.name : '';
  }
  if (!lead) {
    const firstReq = baseRows.find(r => !r.isOptional) || baseRows[0];
    const ing = firstReq ? ingredientsById[firstReq.ingredientId] : null;
    lead = ing ? ing.name : 'Spezial';
  }
  // „Rindfleisch" → „Rindfleisch", knapp halten (erstes Wort).
  lead = String(lead).split(/\s|·/)[0];
  return `${adj} ${lead}${KIND_SUFFIX[kind] || ''}`.trim();
}
